import React, { FC, useEffect, useState, useRef } from 'react';
import { Button, Input, Icon, Modal, Popup } from 'semantic-ui-react';
import { Flexbox, FlexItem, StyledContainer } from '@src/views/containers';
import { FlexAlign, FlexJustify, Size, CorvalUIColor, SemanticUISize } from '@src/types';
// @ts-ignore
import sparkleSvg from '@src/_assets/images/sparkle.svg';
import { ChatMessage, useAiContext } from './AiContext';
import Markdown from 'markdown-to-jsx';
import { useCurrentUser, usePrefStates } from '@src/hooks';
import { UserAvatar } from '@src/views/components/user-avatar/UserAvatar';
import { buildLookupEntitiesForAiRequest, normalizePrefId } from './aiLookupEntities';

interface AIChatBoxProps {
  agentType: string;
  /** @deprecated Resolved at send time from context + prefs; kept for compatibility. */
  lookupEntities?: { objectType: string; objectId: string }[];
  allowedLookupObjectTypes?: string[];
  onAssistantMessage?: (msg: ChatMessage) => void;
}

const emptyAgentState = { messages: [], threadId: null, isLoading: false };
const noop = async (..._args: any[]) => {};

const sanitizeMetadataText = (text: string): string => {
  const lines = text.split('\n');
  const kept: string[] = [];
  let skippingMetadataBlock = false;

  for (const line of lines) {
    const trimmed = line.trim();
    const startsMetadataBlock = /^(?:[>\-*\s`#]*)(?:\*{1,2}|_{1,2})?(keysUsed|sourcesUsed|keys_used|sources_used)(?:\*{1,2}|_{1,2})?\s*:/i.test(trimmed);

    if (startsMetadataBlock) {
      skippingMetadataBlock = true;
      continue;
    }

    if (skippingMetadataBlock) {
      const isContinuation =
        trimmed.length === 0 ||
        /^[-*]/.test(trimmed) ||
        /^[\[{\"]/.test(trimmed) ||
        /^(?:[>\-*\s`#]*)(?:\*{1,2}|_{1,2})?(keysUsed|sourcesUsed|keys_used|sources_used)(?:\*{1,2}|_{1,2})?\s*:/i.test(trimmed);

      if (isContinuation) {
        continue;
      }

      skippingMetadataBlock = false;
    }

    kept.push(line);
  }

  return kept.join('\n').trimEnd();
};

const redactDiagnosticFields = (value: any): any => {
  const hiddenField = /^(conversationId|threadId|requestThreadId|matchedConversationThreadId)$/i;

  if (Array.isArray(value)) {
    return value.map((item) => redactDiagnosticFields(item));
  }

  if (value && typeof value === 'object') {
    return Object.entries(value).reduce((acc: Record<string, any>, [key, fieldValue]) => {
      if (hiddenField.test(key)) {
        return acc;
      }

      acc[key] = redactDiagnosticFields(fieldValue);
      return acc;
    }, {});
  }

  return value;
};

const AIChatBox: FC<AIChatBoxProps> = ({
  agentType,
  lookupEntities: _lookupEntitiesProp = [],
  allowedLookupObjectTypes,
  onAssistantMessage,
}) => {
  const ctx = useAiContext();
  const selectedObjects = ctx.selectedObjects ?? [];
  const [, prefs, prefEnums] = usePrefStates();
  const prefProjectId = normalizePrefId(prefs.get(prefEnums.PROJECT_ID));
  const [, projectSpecificPrefs] = usePrefStates(prefProjectId);
  const prefProjectVersionId = normalizePrefId(
    projectSpecificPrefs.get(prefEnums.PROJECT_VERSION_ID)
  );

  const resolveLookupEntities = () =>
    buildLookupEntitiesForAiRequest(selectedObjects, {
      projectId: prefProjectId || undefined,
      projectVersionId: prefProjectVersionId || undefined,
      allowedObjectTypes: allowedLookupObjectTypes,
    });

  const getAgent = typeof ctx.getAgent === 'function' ? ctx.getAgent : () => emptyAgentState;
  const updateAgent = typeof ctx.updateAgent === 'function' ? ctx.updateAgent : noop;
  const handleSendMessage = typeof ctx.handleSendMessage === 'function' ? ctx.handleSendMessage : noop;
  const handleSendLookupAiMessage = typeof ctx.handleSendLookupAiMessage === 'function' ? ctx.handleSendLookupAiMessage : noop;
  const handleBasicAiMessage = typeof ctx.handleBasicAiMessage === 'function' ? ctx.handleBasicAiMessage : noop;
  const { currentUser } = useCurrentUser();
  const agentState = getAgent(agentType);
  const messages = agentState.messages || [];
  const isLoading = agentState.isLoading || false;
  const [message, setMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const showThinking = isLoading || isProcessing;
  const [dataModalOpen, setDataModalOpen] = useState(false);
  const [activeLookupDocs, setActiveLookupDocs] = useState<any[] | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const lastAssistantMessageIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (typeof onAssistantMessage !== 'function' || messages.length === 0) {
      return;
    }

    const latestAssistant = [...messages].reverse().find((m) => !m.isUser && !m.isHeader);
    if (!latestAssistant) {
      return;
    }

    if (latestAssistant.id === lastAssistantMessageIdRef.current) {
      return;
    }

    lastAssistantMessageIdRef.current = latestAssistant.id;
    onAssistantMessage(latestAssistant);
  }, [messages, onAssistantMessage]);

  const getDefaultMessage = (agentType: string): ChatMessage => {
    switch (agentType) {
      case 'activity':
        return {
          id: 'default-activity',
          text:
            "Hi! I'm your AI Assistant.\n\nI can help you compare project versions, analyze costs, review activities, and answer questions about your project data.\n\nHere are the datasets currently available and sample questions you can ask. Before asking a question, make sure you select the correct Object for the dataset you want to use.\n\n**Dataset 1 — Project Version Cost Analysis**\nObject to Select: Any Project\n\nUse this dataset to compare project versions, costs, durations, and business function spending.\n\nExample questions:\n\n- Compare costs across project versions\n- Which version is the most expensive?\n- Give me an executive summary of the project versions.\n- Compare annual spending patterns across all versions\n- Which business functions drive the highest costs?\n\n**Dataset 2 — Project Version Activity Comparison**\nObject to Select: Any Project\n\nUse this dataset to compare activities and changes between project versions.\n\nExample questions:\n\n- What changed between (version A) and (version B)?\n- Which activities were added or removed?\n- Which version differs the most from (version A)?\n\n**Dataset 3 — Budget Costs by Milestone & Business Function**\nObject to Select: Any Project Version\n\nUse this dataset to analyze operational costs between milestones and business functions.\n\nExample questions:\n\n- Which milestone phase has the highest cost?\n- What are the costs between milestones by business function?\n- Which business functions drive the highest milestone costs?\n\n**Dataset 4 — Activities by Business Function**\nObject to Select: Any Project Version\n\nUse this dataset to review activity distribution across business functions.\n\nExample questions:\n\n- Which business function has the most activities?\n- Summarize activity counts across business functions\n\n**Dataset 5 — Activity Details**\nObject to Select: Any Activity or Multiple Activities\n\nUse this dataset to review detailed activity information, schedules, costs, and milestones.\n\nExample questions:\n\n- Summarize this activity\n- What is the duration of this activity?\n- What is the total cost of this activity?\n- What business function owns this activity?\n- Which milestone is this activity closest to?\n\n**Important Notes:**\n- The AI does not currently remember previous questions, so each question should be asked independently.\n- Selecting the correct Object before asking your question is important for accurate results.",
          isUser: false,
          timestamp: new Date()
        };
      case 'project':
        return {
          id: 'default-project',
          text:
            "Hi! I'm your AI Assistant.\n\nI can help you compare project versions, analyze costs, review activities, and answer questions about your project data.\n\nHere are the datasets currently available and sample questions you can ask. Before asking a question, make sure you select the correct Object for the dataset you want to use.\n\n**Dataset 1 — Project Version Cost Analysis**\nObject to Select: Any Project\n\nUse this dataset to compare project versions, costs, durations, and business function spending.\n\nExample questions:\n\n- Compare costs across project versions\n- Which version is the most expensive?\n- Give me an executive summary of the project versions.\n- Compare annual spending patterns across all versions\n- Which business functions drive the highest costs?\n\n**Dataset 2 — Project Version Activity Comparison**\nObject to Select: Any Project\n\nUse this dataset to compare activities and changes between project versions.\n\nExample questions:\n\n- What changed between (version A) and (version B)?\n- Which activities were added or removed?\n- Which version differs the most from (version A)?\n\n**Dataset 3 — Budget Costs by Milestone & Business Function**\nObject to Select: Any Project Version\n\nUse this dataset to analyze operational costs between milestones and business functions.\n\nExample questions:\n\n- Which milestone phase has the highest cost?\n- What are the costs between milestones by business function?\n- Which business functions drive the highest milestone costs?\n\n**Dataset 4 — Activities by Business Function**\nObject to Select: Any Project Version\n\nUse this dataset to review activity distribution across business functions.\n\nExample questions:\n\n- Which business function has the most activities?\n- Summarize activity counts across business functions\n\n**Dataset 5 — Activity Details**\nObject to Select: Any Activity or Multiple Activities\n\nUse this dataset to review detailed activity information, schedules, costs, and milestones.\n\nExample questions:\n\n- Summarize this activity\n- What is the duration of this activity?\n- What is the total cost of this activity?\n- What business function owns this activity?\n- Which milestone is this activity closest to?\n\n**Important Notes:**\n- The AI does not currently remember previous questions, so each question should be asked independently.\n- Selecting the correct Object before asking your question is important for accurate results.",
          isUser: false,
          timestamp: new Date()
        };
      case 'dashboard':
        return {
          id: 'default-dashboard',
          text:
            "Hi! I'm your AI Assistant.\n\nI can help you compare project versions, analyze costs, review activities, and answer questions about your project data.\n\nHere are the datasets currently available and sample questions you can ask. Before asking a question, make sure you select the correct Object for the dataset you want to use.\n\n**Dataset 1 — Project Version Cost Analysis**\nObject to Select: Any Project\n\nUse this dataset to compare project versions, costs, durations, and business function spending.\n\nExample questions:\n\n- Compare costs across project versions\n- Which version is the most expensive?\n- Give me an executive summary of the project versions.\n- Compare annual spending patterns across all versions\n- Which business functions drive the highest costs?\n\n**Dataset 2 — Project Version Activity Comparison**\nObject to Select: Any Project\n\nUse this dataset to compare activities and changes between project versions.\n\nExample questions:\n\n- What changed between (version A) and (version B)?\n- Which activities were added or removed?\n- Which version differs the most from (version A)?\n\n**Dataset 3 — Budget Costs by Milestone & Business Function**\nObject to Select: Any Project Version\n\nUse this dataset to analyze operational costs between milestones and business functions.\n\nExample questions:\n\n- Which milestone phase has the highest cost?\n- What are the costs between milestones by business function?\n- Which business functions drive the highest milestone costs?\n\n**Dataset 4 — Activities by Business Function**\nObject to Select: Any Project Version\n\nUse this dataset to review activity distribution across business functions.\n\nExample questions:\n\n- Which business function has the most activities?\n- Summarize activity counts across business functions\n\n**Dataset 5 — Activity Details**\nObject to Select: Any Activity or Multiple Activities\n\nUse this dataset to review detailed activity information, schedules, costs, and milestones.\n\nExample questions:\n\n- Summarize this activity\n- What is the duration of this activity?\n- What is the total cost of this activity?\n- What business function owns this activity?\n- Which milestone is this activity closest to?\n\n**Important Notes:**\n- The AI does not currently remember previous questions, so each question should be asked independently.\n- Selecting the correct Object before asking your question is important for accurate results.",
          isUser: false,
          timestamp: new Date()
        };
      case 'assistant':
        return {
          id: 'default-assistant',
          text:
            "Hi! I'm your AI Assistant.\n\nI can help you compare project versions, analyze costs, review activities, and answer questions about your project data.\n\nHere are the datasets currently available and sample questions you can ask. Before asking a question, make sure you select the correct Object for the dataset you want to use.\n\n**Dataset 1 — Project Version Cost Analysis**\nObject to Select: Any Project\n\nUse this dataset to compare project versions, costs, durations, and business function spending.\n\nExample questions:\n\n- Compare costs across project versions\n- Which version is the most expensive?\n- Give me an executive summary of the project versions.\n- Compare annual spending patterns across all versions\n- Which business functions drive the highest costs?\n\n**Dataset 2 — Project Version Activity Comparison**\nObject to Select: Any Project\n\nUse this dataset to compare activities and changes between project versions.\n\nExample questions:\n\n- What changed between (version A) and (version B)?\n- Which activities were added or removed?\n- Which version differs the most from (version A)?\n\n**Dataset 3 — Budget Costs by Milestone & Business Function**\nObject to Select: Any Project Version\n\nUse this dataset to analyze operational costs between milestones and business functions.\n\nExample questions:\n\n- Which milestone phase has the highest cost?\n- What are the costs between milestones by business function?\n- Which business functions drive the highest milestone costs?\n\n**Dataset 4 — Activities by Business Function**\nObject to Select: Any Project Version\n\nUse this dataset to review activity distribution across business functions.\n\nExample questions:\n\n- Which business function has the most activities?\n- Summarize activity counts across business functions\n\n**Dataset 5 — Activity Details**\nObject to Select: Any Activity or Multiple Activities\n\nUse this dataset to review detailed activity information, schedules, costs, and milestones.\n\nExample questions:\n\n- Summarize this activity\n- What is the duration of this activity?\n- What is the total cost of this activity?\n- What business function owns this activity?\n- Which milestone is this activity closest to?\n\n**Important Notes:**\n- The AI does not currently remember previous questions, so each question should be asked independently.\n- Selecting the correct Object before asking your question is important for accurate results.",
          isUser: false,
          timestamp: new Date()
        };
      default:
        return {
          id: 'default-generic',
          text: 'Hello! How can I assist you today?',
          isUser: false,
          timestamp: new Date()
        };
    }
  };

  const hardcodedBryntumJson = {
    tasks: {
      rows: [
        {
          id: '1',
          name: 'Task A',
          startDate: '2025-08-01',
          endDate: '2025-08-03',
          duration: 3,
          percentDone: 50,
          parentId: null
        },
        {
          id: '2',
          name: 'Task B',
          startDate: '2025-08-04',
          endDate: '2025-08-06',
          duration: 3,
          percentDone: 20,
          parentId: null
        }
      ]
    },
    dependencies: {
      rows: [
        {
          id: 'd1',
          from: '1',
          to: '2',
          type: 2
        }
      ]
    }
  };

  const sendMessage = async () => {
    if (!message.trim()) return;
    const msg = message.trim();
    setMessage('');
    setIsProcessing(true);
    if (agentType === 'dashboard') {
      await handleBasicAiMessage(
        agentType,
        msg,
        'You are a data analysis agent. Help users interpret data from their biopharma commercialization dashboard. The datasets will come from tables/charts from a dashboard to tell them about their commercialiation map. Give me insights, analysis, and recommendations based on this dataset. Keep answers short, no more than 3 sentances.',
        false,
        ''
      );
    } else {
      await handleSendLookupAiMessage(agentType, msg, resolveLookupEntities(), false, '');
    }

    setIsProcessing(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && message.trim() && !isProcessing) {
      sendMessage();
    }
  };

  const normalizeSqlNewlines = (text: string): string =>
    text
      .replace(/CHAR\s*\(\s*10\s*\)/gi, '\n')
      .replace(/\\r\\n/g, '\n')
      .replace(/\\n/g, '\n')
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n');

  const formatFullContentForDisplay = (text: string): string => {
    let formatted = normalizeSqlNewlines(text);

    formatted = formatted
      .replace(/\s*\|\|\s*/g, '\n')
      .replace(/\s+(PROJECT VERSION SUMMARY|MILESTONES|BUSINESS FUNCTION TOTALS|YEAR BREAKDOWN|QUARTER BREAKDOWN)\s+/gi, '\n\n$1\n')
      .replace(/\s*(-{3,})\s*/g, '\n$1\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();

    return formatted;
  };

  const getFullContentValue = (doc: any): string | null => {
    if (doc == null || typeof doc !== 'object') {
      return doc == null ? null : String(doc);
    }

    for (const [key, value] of Object.entries(doc)) {
      const normalizedKey = key.replace(/_/g, '').toLowerCase();
      if (normalizedKey === 'fullcontent') {
        if (value == null) {
          return null;
        }
        return formatFullContentForDisplay(String(value));
      }
    }

    return null;
  };

  const renderJsonValue = (value: any, depth: number = 0, label?: string): React.ReactNode => {
    const indent = depth * 12;
    const summaryStyle: React.CSSProperties = {
      cursor: 'pointer',
      fontWeight: 700,
      background: '#f6f2fb',
      border: '1px solid #e6d9f5',
      padding: '4px 8px',
      borderRadius: 6,
      display: 'inline-block'
    };
    const contentStyle: React.CSSProperties = {
      marginTop: 6,
      marginLeft: 12,
      paddingLeft: 8,
      borderLeft: '2px solid #e6d9f5'
    };

    if (value === null || value === undefined) {
      return (
        <div style={{ marginLeft: indent, color: '#888' }}>
          {label ? <span style={{ fontWeight: 700 }}>{label}:</span> : null}{' '}
          {String(value)}
        </div>
      );
    }

    if (Array.isArray(value)) {
      const header = label ? `${label} [${value.length}]` : `Array [${value.length}]`;
      return (
        <div style={{ marginLeft: indent, marginBottom: 8 }}>
          <details>
            <summary style={summaryStyle}>{header}</summary>
            <div style={contentStyle}>
              {value.map((item, index) => (
                <div key={`arr-${depth}-${index}`} style={{ marginBottom: 6 }}>
                  {renderJsonValue(item, depth + 1, `[${index}]`)}
                </div>
              ))}
            </div>
          </details>
        </div>
      );
    }

    if (typeof value === 'object') {
      if (!label && depth === 0) {
        return (
          <div>
            {Object.entries(value)
              .filter(([key]) => !key.includes('_Id'))
              .map(([key, val]) => (
                <div key={`obj-${depth}-${key}`} style={{ marginBottom: 6 }}>
                  {renderJsonValue(val, depth + 1, key)}
                </div>
              ))}
          </div>
        );
      }

      const header = label ?? 'Object';
      return (
        <div style={{ marginLeft: indent, marginBottom: 8 }}>
          <details>
            <summary style={summaryStyle}>{header}</summary>
            <div style={contentStyle}>
              {Object.entries(value)
                .filter(([key]) => !key.includes('_Id'))
                .map(([key, val]) => (
                  <div key={`obj-${depth}-${key}`} style={{ marginBottom: 6 }}>
                    {renderJsonValue(val, depth + 1, key)}
                  </div>
                ))}
            </div>
          </details>
        </div>
      );
    }

    return (
      <div style={{ marginLeft: indent, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {label ? <span style={{ fontWeight: 700 }}>{label}:</span> : null}{' '}
        {String(value)}
      </div>
    );
  };

  const renderMessage = (msg: ChatMessage) => {
    if (msg.isHeader) {
      return null;
    }

    const hasLookupDocs = !msg.isUser && (msg.lookupDocs?.length ?? 0) > 0;
    const timestampStr = msg.timestamp
      ? new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : '';
    const sanitizedText = sanitizeMetadataText(msg.text || '');
    const showDebugInfo = !msg.isUser;
    const rawDebug = msg.debug ?? { note: 'No diagnostics are available for this historical message.' };
    const displayDebug = redactDiagnosticFields(rawDebug);
    const sourceIndexes = !msg.isUser
      ? (Array.isArray(msg.sourceIndexes) && msg.sourceIndexes.length > 0
          ? msg.sourceIndexes
          : Array.isArray(msg.selectedIndexes)
            ? msg.selectedIndexes
            : [])
      : [];
    const hasRecordsByIndex = !msg.isUser && !!msg.recordsByIndex && Object.keys(msg.recordsByIndex).length > 0;
    const hasSourceInfo = !msg.isUser && (hasLookupDocs || sourceIndexes.length > 0 || hasRecordsByIndex);

    return (
      <React.Fragment key={msg.id}>
        <StyledContainer className={`ai-chat-message ${msg.isUser ? 'user' : 'ai'}`} style={{ marginBottom: 24, position: 'relative' }}>
          <Flexbox justify={msg.isUser ? FlexJustify.END : FlexJustify.START} gap={Size.SM} align={FlexAlign.START}>
            {!msg.isUser && (
              <StyledContainer
                className="ai-chat-avatar"
                bgColor={CorvalUIColor.PURPLEBACKGROUND}
                style={{
                  borderRadius: '50%',
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  marginTop: 2,
                  flexShrink: 0,
                }}
              >
                <img src={sparkleSvg} alt="AI" style={{ width: 18, height: 18 }} />
              </StyledContainer>
            )}

            <StyledContainer className="ai-chat-message-container">
              <div
                style={{
                  fontSize: 11,
                  color: '#999',
                  marginBottom: 4,
                  textAlign: msg.isUser ? 'right' : 'left',
                  display: 'flex',
                  justifyContent: msg.isUser ? 'flex-end' : 'flex-start',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <span>{timestampStr}</span>
                {showDebugInfo && (
                  <Popup
                    hoverable
                    position={msg.isUser ? 'top right' : 'top left'}
                    trigger={<Icon name="question circle outline" style={{ margin: 0, cursor: 'help', color: '#7a7a7a' }} />}
                    content={
                      <div style={{ width: 860, maxWidth: '90vw' }}>
                        <div style={{ fontWeight: 700, marginBottom: 10, fontSize: 14 }}>AI Call Diagnostics</div>
                        <div
                          style={{
                            fontSize: 12,
                            lineHeight: 1.5,
                            background: '#f8f8fb',
                            border: '1px solid #e3e3ef',
                            borderRadius: 8,
                            padding: '8px 10px',
                            marginBottom: 10,
                          }}
                        >
                          <div><strong>Decision:</strong> {displayDebug?.decision ?? 'N/A'}</div>
                          <div><strong>Started New Conversation:</strong> {String(displayDebug?.startedNewConversation ?? false)}</div>
                          <div><strong>Reused Dataset:</strong> {String(displayDebug?.reusedDataset ?? false)}</div>
                          <div>
                            <strong>Indexes:</strong>{' '}
                            {Array.isArray(displayDebug?.selectedIndexes) && displayDebug.selectedIndexes.length > 0
                              ? displayDebug.selectedIndexes.join(', ')
                              : 'N/A'}
                          </div>
                          <div>
                            <strong>Reasons:</strong>{' '}
                            {Array.isArray(displayDebug?.decisionReasons) && displayDebug.decisionReasons.length > 0
                              ? displayDebug.decisionReasons.join(', ')
                              : 'N/A'}
                          </div>
                        </div>
                        <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 12 }}>Raw Details</div>
                        <pre
                          style={{
                            margin: 0,
                            whiteSpace: 'pre-wrap',
                            fontSize: 12,
                            lineHeight: 1.45,
                            maxHeight: 520,
                            overflowY: 'auto',
                            background: '#fff',
                            border: '1px solid #ddd',
                            borderRadius: 8,
                            padding: 12,
                          }}
                        >
                          {JSON.stringify(displayDebug, null, 2)}
                        </pre>
                      </div>
                    }
                  />
                )}
              </div>

              <StyledContainer
                className={`ai-chat-bubble ${msg.isUser ? 'user' : 'ai'}`}
                padding={[Size.MD, Size.MD, Size.MD, Size.MD]}
                style={{
                    borderRadius: msg.isUser ? '12px 12px 0 12px' : '0 12px 12px 12px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                    backgroundColor: msg.isUser ? '#f0f0f0' : '#fff',
                    border: msg.isUser ? 'none' : '1px solid #eee'
                }}
              >
                <span className="ai-chat-text">
                  <Markdown>{sanitizedText}</Markdown>
                </span>
              </StyledContainer>

            {hasSourceInfo && (
              <StyledContainer margin={[Size.XS, null, null, null]} padding={[null, null, null, Size.XS]}>
                <Flexbox align={FlexAlign.CENTER} style={{ gap: 6, fontSize: 12, color: '#666', marginBottom: 4 }}>
                  <Icon name="database" size="small" />
                  <span style={{ fontWeight: 600 }}>Sources</span>
                </Flexbox>
                {sourceIndexes.length > 0 && (
                  <div style={{ fontSize: 12, color: '#555', marginBottom: 6 }}>
                    <strong>Indexes:</strong> {sourceIndexes.join(', ')}
                  </div>
                )}
                {hasRecordsByIndex && (
                  <div style={{ fontSize: 12, color: '#555', marginBottom: 6 }}>
                    <strong>Records by index:</strong>{' '}
                    {Object.entries(msg.recordsByIndex ?? {})
                      .map(([indexName, count]) => `${indexName} (${count})`)
                      .join(', ')}
                  </div>
                )}
                {hasLookupDocs && (
                  <Button
                    basic
                    compact
                    size="mini"
                    onClick={() => {
                      setActiveLookupDocs(msg.lookupDocs ?? []);
                      setDataModalOpen(true);
                    }}
                  >
                    View Source Data ({msg.lookupDocs?.length ?? 0} records)
                  </Button>
                )}
              </StyledContainer>
            )}
            </StyledContainer>

            {msg.isUser && (
              <StyledContainer className="ai-chat-user-avatar" style={{ marginLeft: 4, marginTop: 2, flexShrink: 0 }}>
                {currentUser ? (
                  <UserAvatar
                    user={currentUser}
                    asInitials
                    rounded
                    size={SemanticUISize.MINI}
                    avatarSize={24}
                  />
                ) : (
                  <StyledContainer
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor: '#e9e9e9',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon name="user" size="small" style={{ margin: 0, color: '#777' }} />
                  </StyledContainer>
                )}
              </StyledContainer>
            )}
        </Flexbox>
      </StyledContainer>
      <div style={{ borderBottom: '1px solid #eee', marginBottom: 24, width: '100%' }} />
    </React.Fragment>
    );
  };

  const renderThinkingIndicator = () => (
    <StyledContainer
      className="ai-chat-message ai ai-chat-thinking"
      style={{ marginBottom: 24 }}
      aria-live="polite"
      aria-busy="true"
    >
      <Flexbox justify={FlexJustify.START} gap={Size.SM} align={FlexAlign.START}>
        <StyledContainer className="ai-chat-message-container">
          <Flexbox align={FlexAlign.CENTER} gap={Size.XS} style={{ marginBottom: 4 }}>
            <StyledContainer
              className="ai-chat-avatar"
              bgColor={CorvalUIColor.PURPLEBACKGROUND}
              style={{
                borderRadius: '50%',
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}
            >
              <img src={sparkleSvg} alt="AI" style={{ width: 18, height: 18 }} />
            </StyledContainer>
            <span className="ai-chat-thinking-label">Thinking</span>
          </Flexbox>
          <StyledContainer
            className="ai-chat-bubble ai ai-chat-thinking-bubble"
            padding={[Size.MD, Size.MD, Size.MD, Size.MD]}
            style={{
              borderRadius: '0 12px 12px 12px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              backgroundColor: '#fff',
              border: '1px solid #eee',
            }}
          >
            <span className="ai-chat-thinking-dots" aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
          </StyledContainer>
        </StyledContainer>
      </Flexbox>
    </StyledContainer>
  );

  const messagesContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages, showThinking]);

  const lastEmittedIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!messages || messages.length === 0) return;
    const lastAssistant = [...messages].reverse().find((m) => !m.isUser && !m.isHeader);
    if (!lastAssistant) return;
    if (lastAssistant.id.startsWith('default-')) return; // ignore intro
    if (lastAssistant.id !== lastEmittedIdRef.current) {
      lastEmittedIdRef.current = lastAssistant.id;
      onAssistantMessage?.(lastAssistant);
    }
  }, [messages, onAssistantMessage]);

  return (
    <StyledContainer className="ai-chat-box" bgColor={CorvalUIColor.WHITE} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <StyledContainer
        ref={messagesContainerRef}
        className="ai-chat-messages"
        padding={[Size.MD, Size.MD, Size.MD, Size.MD]}
        style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
        {renderMessage(getDefaultMessage(agentType))}
        {messages?.map(renderMessage)}
        {showThinking && renderThinkingIndicator()}
      </StyledContainer>

      <StyledContainer
        className="ai-chat-input-container"
        padding={[Size.SM, Size.SM, Size.SM, Size.SM]}
        borderSize={[Size.SM, null, null, null]}
        borderColor={CorvalUIColor.DK_SCREEN_25}
        bgColor={CorvalUIColor.WHITE}
      >
        <Flexbox align={FlexAlign.CENTER} gap={Size.MD} className="ai-chat-input-wrapper" style={{ width: '100%' }}>
          <Input
            fluid
            placeholder="Ask me anything..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={showThinking}
            className="ai-chat-input"
            autoFocus
            style={{ flex: 1 }}
          />
          <Button
            icon="arrow up"
            color="purple"
            disabled={!message.trim() || showThinking}
            loading={showThinking}
            onClick={sendMessage}
            className="ai-chat-send-btn"
            style={{ minWidth: 'auto', marginLeft: '12px' }}
          />
        </Flexbox>
      </StyledContainer>

      <Modal open={dataModalOpen} onClose={() => setDataModalOpen(false)} size="large" closeIcon>
        <Modal.Header>Source Data</Modal.Header>
        <Modal.Content scrolling>
          {(activeLookupDocs ?? []).map((doc, index) => {
            const displayValue = getFullContentValue(doc) ?? doc;
            return (
              <details key={`lookup-doc-${index}`} style={{ marginBottom: 12 }}>
                <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
                  Record {index + 1}
                </summary>
                <StyledContainer margin={[Size.XS, null, null, null]} style={{ fontSize: 12 }}>
                  {renderJsonValue(displayValue)}
                </StyledContainer>
              </details>
            );
          })}
        </Modal.Content>
      </Modal>
    </StyledContainer>
  );
};

export default AIChatBox;